import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { FloatingInput } from '@/components/ui/floating-input'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { apiFetch } from '@/utils'
import TermsPage from '@/pages/TermsPage'
import logo from '@/assets/logo-login.svg';
import { Input } from '@/components/ui/input'

const signupFormSchema = z
	.object({
		email: z
			.string()
			.email('Invalid email address')
			.max(64, 'Email must be at most 64 characters'),
		username: z
			.string()
			.min(3, 'Username must be 3-20 characters')
			.max(20, 'Username must be 3-20 characters')
			.regex(
				/^(?=.{3,20}$)(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/,
				'Username can only contain letters, numbers, dots, and underscores, and cannot start/end with or have consecutive dots/underscores'
			),
		firstName: z
			.string()
			.min(1, 'First name is required')
			.max(16, 'First name must be at most 16 characters'),
		lastName: z
			.string()
			.min(1, 'Last name is required')
			.max(32, 'Last name must be at most 32 characters'),
		password: z
			.string()
			.min(8, 'Password must be at least 8 characters')
			.regex(
				/^(?=.*[!@#$%^&*-])(?=.*[0-9])(?=.*[A-Z]).{8,}$/,
				'Password must contain at least one uppercase letter, one number, and one special character'
			),
		confirmPassword: z.string().min(8, 'Password confirmation is required'),
		acceptTerms: z.literal(true, {
			errorMap: () => ({ message: 'You must accept the Terms of Service to continue' })
		})
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ['confirmPassword']
	})

type SignupFormData = z.infer<typeof signupFormSchema>

export default function SignupPage() {
	const [isLoading, setIsLoading] = useState(false)
	const [tosOpen, setTosOpen] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()

	const form = useForm<SignupFormData>({
		resolver: zodResolver(signupFormSchema),
		defaultValues: {
			email: '',
			username: '',
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: '',
			acceptTerms: false as unknown as true
		}
	})

	const onSubmit = async (data: SignupFormData) => {
		setIsLoading(true)
		try {
			const signupResponse = await apiFetch('/v1/users', {
				method: 'POST',
				body: JSON.stringify({
					email: data.email,
					username: data.username,
					firstName: data.firstName,
					lastName: data.lastName,
					password: data.password
				})
			})
			const { error, success } = await signupResponse.json()
			if (!success || !signupResponse.ok) {
				throw new Error(error?.message || 'Failed to create user')
			}

			// Belt-and-suspenders: ask the backend to (re)send the verification email
			// in case it isn't dispatched as part of the registration response.
			await apiFetch('/v1/users/send-verification', {
				method: 'POST',
				body: JSON.stringify({ email: data.email })
			})

			toast({
				title: 'Account created',
				description:
					'Check your email for a verification link before signing in.'
			})
			navigate(`/login?unverified=${encodeURIComponent(data.email)}`)
		} catch (error: any) {
			toast({
				title: 'Registration failed',
				description: error?.details || error.message || 'Registration failed',
				variant: 'destructive'
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-screen flex flex-col items-center login-page">
			<div className="flex flex-col justify-center items-center w-full py-8">
				<img src={logo} alt="" />
				<p className="text-[20px] leading-6 font-bold text-[#ffffff] font-montserrat mt-1 mb-1">SECH</p>
				<p className="text-xs leading-[14px] font-montserrat font-medium text-[#FFFFFF]">Digital Wallet</p>
			</div>
			<div className="max-w-[1024px] w-full flex-1">
				<Card className="bg-[#2F2F32] border-0 rounded-tl-[36px] rounded-tr-[36px] rounded-b-none p-6 min-h-full">
					<div className="text-center mb-6">
						<h1 className="text-[20px] leading-[26px] font-semibold text-[#ffffff] font-figtree">Create an Account</h1>
					</div>

						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)}>

								{/* Email */}
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">Email</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="email"
													autoComplete="email"
													placeholder="Enter email"
													className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]   focus-visible:outline-none
																focus-visible:ring-2
																focus-visible:ring-[#6B6565]
																focus-visible:ring-offset-0 text-[#ffffff]"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="username"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">Username</FormLabel>
											<FormControl>
												<Input {...field}
												placeholder="Enter username"
												className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]  focus-visible:outline-none
															focus-visible:ring-2
															focus-visible:ring-[#6B6565]
															focus-visible:ring-offset-0 text-[#ffffff]"/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="firstName"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">First Name</FormLabel>
											<FormControl>
												<Input
													placeholder="Enter first name"
													{...field}
													autoComplete="given-name"
													className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]  focus-visible:outline-none
																focus-visible:ring-2
																focus-visible:ring-[#6B6565]
																focus-visible:ring-offset-0 text-[#ffffff]"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="lastName"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">Last Name</FormLabel>
											<FormControl>
												<Input
													placeholder="Enter last name"
													{...field}
													autoComplete="family-name"
													className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]  focus-visible:outline-none
																focus-visible:ring-2
																focus-visible:ring-[#6B6565]
																focus-visible:ring-offset-0 text-[#ffffff]"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">Password</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder="Enter password"
													type="password"
													autoComplete="new-password"
													className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)] focus-visible:outline-none
																focus-visible:ring-2
																focus-visible:ring-[#6B6565]
																focus-visible:ring-offset-0 text-[#ffffff]"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="confirmPassword"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">Confirm Password</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder="Confirm password"
													type="password"
													autoComplete="new-password"
													className="bg-[#212124] border-0 h-[42px] rounded-[8px] mx-1 w-[calc(100%-8px)]  focus-visible:outline-none
																focus-visible:ring-2
																focus-visible:ring-[#6B6565]
																focus-visible:ring-offset-0 text-[#ffffff]"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="acceptTerms"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0 mb-5 pl-[2px]">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
													className="border-[#4C4C4C] data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444]"
												/>
											</FormControl>

											<div className="space-y-1 leading-none">
												<FormLabel className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF]">
													I agree to the{' '}
													<Dialog open={tosOpen} onOpenChange={setTosOpen}>
														<DialogTrigger asChild>
															<Button
																type="button"
																variant="link"
																className="p-0 h-auto font-semibold font-figtree text-xs leading-4 text-[#EF4444] hover:text-[#DC2626]"
															>
																Terms of Service
															</Button>
														</DialogTrigger>

														<DialogContent className="max-h-[80vh] flex flex-col bg-[#18181B] border-[#4C4C4C]">
															<div className="overflow-y-auto flex-1">
																<TermsPage />
															</div>

															<DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
																<Button
																	type="button"
																	variant="outline"
																	className="border-[#4C4C4C] bg-[#2F2F32] text-white hover:bg-[#3C3C3C]"
																	onClick={() => setTosOpen(false)}
																>
																	Close
																</Button>

																<Button
																	type="button"
																	className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF]"
																	onClick={() => {
																		field.onChange(true)
																		setTosOpen(false)
																	}}
																>
																	I Agree
																</Button>
															</DialogFooter>
														</DialogContent>
													</Dialog>
												</FormLabel>

												<FormMessage />
											</div>
										</FormItem>
									)}
								/>

								<Button
									type="submit"
									className="w-full h-[42px] text-sm leading-[14px] font-medium font-figtree mb-6 text-[#ffffff] rounded-[21px] bg-[#EF4444]"
									disabled={isLoading}
								>
									{isLoading ? 'Creating Account...' : 'Create Account'}
								</Button>

							</form>
						</Form>

					<div className="text-center">
						<p className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF]">
							Already have an account?{' '}
							<Button
								variant="link"
								className="p-0 h-auto font-semibold font-figtree text-xs leading-4 text-[#EF4444] hover:text-[#DC2626]"
								onClick={() => navigate('/login')}>
								Log in
							</Button>
						</p>
					</div>
				</Card>
			</div>
		</div>
	)
}